import { Check, Circle } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import type { PasswordRequirement } from './passwordPolicy';

export interface PasswordRequirementsProps {
  requirements: PasswordRequirement[];
  className?: string;
}

/**
 * Live password-requirements checklist. Each rule ticks from muted to success as
 * the user types — positive reinforcement that prevents a failed submit and
 * removes the guesswork around the policy (Nielsen #5 error prevention, #6
 * recognition-over-recall). Met/unmet state is announced to assistive tech.
 */
export function PasswordRequirements({ requirements, className }: PasswordRequirementsProps) {
  if (requirements.length === 0) return null;

  return (
    <ul
      aria-label="Password requirements"
      aria-live="polite"
      className={cn('flex flex-col gap-1', className)}
    >
      {requirements.map((req) => (
        <li
          key={req.key}
          aria-label={`${req.label}: ${req.met ? 'met' : 'not met'}`}
          className={cn(
            'flex items-center gap-1.5 text-xs transition-colors',
            req.met ? 'text-success' : 'text-muted-foreground',
          )}
        >
          {req.met ? (
            <Check size={13} className="shrink-0" aria-hidden="true" />
          ) : (
            <Circle size={13} className="shrink-0" aria-hidden="true" />
          )}
          <span>{req.label}</span>
        </li>
      ))}
    </ul>
  );
}
